import React from 'react';
import { Card, CardContent, Typography, Divider, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { toIndianRupeeFormat } from '../custom';

function PortfolioCard() {
    const { clientPositions, loading, hasLoadedOnce } = useSelector((state) => state.portfolio);

    // Only show shimmer if we've NEVER loaded data before.
    // Subsequent polling refreshes (loading=true again) should NOT re-trigger shimmer.
    const showShimmer = !hasLoadedOnce || !clientPositions;

    const {
        ledger_balance = 0,
        available_margin = 0,
        used_margin = 0,
        m2m = 0,
        active_pnl = 0,
        closed_pnl = 0,
        total_pnl = 0,
    } = clientPositions || {};

    const isPositive = (val) => val >= 0;
    const pnlColor = (val) => isPositive(val) ? '#16a34a' : '#dc2626';
    const pnlPrefix = (val) => isPositive(val) ? '+ ₹' : '- ₹';
    const absFormat = (val) => toIndianRupeeFormat(Math.abs(val));

    const renderValue = (value, color) => {
        if (showShimmer) {
            return <Skeleton variant="text" width={60} height={20} />;
        }
        return (
            <Typography variant="body2" style={{ color }} className="font-lato text-[13px]">
                {value}
            </Typography>
        );
    };

    return (
        <Card className="rounded-xl px-3 h-auto m-4 shadow-xl bg-[#FFFFFF]">
            <CardContent sx={{ paddingBottom: '10px !important' }}>

                <div className="flex justify-between">
                    <Typography className="text-[12px] font-semibold">Ledger Balance</Typography>
                    <Typography className="text-[12px] font-semibold">Available Margin</Typography>
                </div>

                <div className="flex justify-between mt-1">
                    {renderValue(`₹ ${toIndianRupeeFormat(ledger_balance)}`, '#006bb3')}
                    {showShimmer
                        ? <Skeleton variant="text" width={60} height={20} />
                        : (
                            <Typography style={{ color: pnlColor(available_margin) }} className="text-[13px]">
                                {pnlPrefix(available_margin)}{absFormat(available_margin)}
                            </Typography>
                        )}
                </div>

                <Divider sx={{ my: '10px' }} />

                <div className="flex justify-between">
                    <Typography className="text-[12px] font-semibold">Used Margin</Typography>
                    <Typography className="text-[12px] font-semibold">M2M</Typography>
                </div>

                <div className="flex justify-between mt-1">
                    {renderValue(`₹ ${toIndianRupeeFormat(used_margin)}`, '#006bb3')}
                    {showShimmer
                        ? <Skeleton variant="text" width={60} height={20} />
                        : (
                            <Typography style={{ color: pnlColor(m2m) }} className="text-[13px]">
                                {pnlPrefix(m2m)}{absFormat(m2m)}
                            </Typography>
                        )}
                </div>

                <Divider sx={{ my: '10px' }} />

                <div className="flex justify-between">
                    <Typography className="text-[12px] font-semibold">Active P&L</Typography>
                    <Typography className="text-[12px] font-semibold">Closed P&L</Typography>
                </div>

                <div className="flex justify-between mt-1">
                    {showShimmer
                        ? <Skeleton variant="text" width={60} height={20} />
                        : (
                            <Typography style={{ color: pnlColor(active_pnl) }}>
                                {pnlPrefix(active_pnl)}{absFormat(active_pnl)}
                            </Typography>
                        )}

                    {showShimmer
                        ? <Skeleton variant="text" width={60} height={20} />
                        : (
                            <Typography style={{ color: pnlColor(closed_pnl) }}>
                                {pnlPrefix(closed_pnl)}{absFormat(closed_pnl)}
                            </Typography>
                        )}
                </div>

            </CardContent>
        </Card>
    );
}

export default PortfolioCard;